import { Component, Input, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RecognitionVisualizationConfig, RecognitionObject } from '../bbox-gallery-card/bbox-gallery-card.component';

// ─────────────────────────────────────────────────────────────────────────────

const TAG_META: Record<string, { label: string; color: string; bg: string }> = {
  postobon:    { label: 'Postobon',    color: '#6366f1', bg: 'rgba(99,102,241,.12)'  },
  competencia: { label: 'Competencia', color: '#f43f5e', bg: 'rgba(244,63,94,.12)'   },
  precios:     { label: 'Precios',     color: '#f59e0b', bg: 'rgba(245,158,11,.12)'  },
  nevera:      { label: 'Nevera',      color: '#14b8a6', bg: 'rgba(20,184,166,.12)'  },
};

export interface TagCoverage {
  key:        string;
  label:      string;
  color:      string;
  bg:         string;
  areaPx:     number;   // área bruta en px²
  areaPct:    number;   // % del área total de la imagen
  barW:       number;   // 0-100 relativo al mayor tag
  objCount:   number;   // objetos que contribuyen (sin Others)
  avgArea:    number;   // área promedio por objeto
}

export interface ObjectAreaRow {
  name:      string;
  tag:       string;
  tagLabel:  string;
  tagColor:  string;
  tagBg:     string;
  areaPx:    number;
  areaPct:   number;
  count:     number;
  avgArea:   number;
  barW:      number;
}

export interface AreaCoverageConfig {
  title: string;
  rows: import('../bbox-gallery-card/bbox-gallery-card.component').RecognitionRow[];
}

@Component({
  selector: 'app-area-coverage-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './area-coverage-card.component.html',
  styleUrl:    './area-coverage-card.component.scss',
})
export class AreaCoverageCardComponent implements OnChanges {
  @Input() config!: RecognitionVisualizationConfig;
  @Input() loading = false;

  // ── KPIs globales ─────────────────────────────────────────────────────────
  totalImageArea   = 0;   // refW × refH × nImages
  coveredArea      = 0;   // suma de todos los bboxes
  overlapWarning   = false;

  // ── Por tag ───────────────────────────────────────────────────────────────
  tagCoverages: TagCoverage[] = [];

  // ── Stacked bar segments ──────────────────────────────────────────────────
  stackSegments: { color: string; pct: number; label: string }[] = [];

  // ── Por objeto (top) ──────────────────────────────────────────────────────
  objectRows:    ObjectAreaRow[] = [];
  showAllObjects = false;
  protected allObjectRows: ObjectAreaRow[] = [];

  ngOnChanges(): void { this.build(); }

  private build(): void {
    if (!this.config?.rows?.length) return;

    const allObjs = this.config.rows.flatMap(r => r.objects);

    // Dimensiones de referencia
    let refW = 1, refH = 1;
    for (const obj of allObjs) {
      if (obj.bbox.x_max > refW) refW = obj.bbox.x_max;
      if (obj.bbox.y_max > refH) refH = obj.bbox.y_max;
    }

    const imageArea     = refW * refH;
    this.totalImageArea = imageArea * this.config.rows.length;

    // ── Cobertura por tag ─────────────────────────────────────────────────

    const tagKeys = [...new Set(allObjs.map(o => o.tag))].filter(t => TAG_META[t]);
    const tagMap: Record<string, { area: number; objs: RecognitionObject[] }> = {};
    tagKeys.forEach(t => { tagMap[t] = { area: 0, objs: [] }; });

    let totalCovered = 0;
    for (const obj of allObjs) {
      if (!tagMap[obj.tag]) continue;
      const area = obj.bbox.width * obj.bbox.height;
      tagMap[obj.tag].area += area;
      tagMap[obj.tag].objs.push(obj);
      totalCovered += area;
    }

    this.coveredArea    = totalCovered;
    this.overlapWarning = totalCovered > this.totalImageArea * 1.2; // overlapping probable

    const maxTagArea = Math.max(...Object.values(tagMap).map(v => v.area), 1);

    this.tagCoverages = tagKeys.map(t => {
      const v       = tagMap[t];
      const named   = v.objs.filter(o => o.name !== 'Others');
      const areaPct = (v.area / this.totalImageArea) * 100;
      return {
        key:      t,
        label:    TAG_META[t].label,
        color:    TAG_META[t].color,
        bg:       TAG_META[t].bg,
        areaPx:   Math.round(v.area),
        areaPct:  Math.round(areaPct * 10) / 10,
        barW:     Math.round((v.area / maxTagArea) * 100),
        objCount: named.length,
        avgArea:  named.length ? Math.round(v.area / named.length) : 0,
      };
    }).sort((a, b) => b.areaPx - a.areaPx);

    // Stacked bar
    const totalForStack = this.tagCoverages.reduce((s, t) => s + t.areaPx, 0) || 1;
    this.stackSegments = this.tagCoverages.map(t => ({
      color: t.color,
      pct:   Math.round((t.areaPx / totalForStack) * 100),
      label: t.label,
    }));

    // ── Por objeto ────────────────────────────────────────────────────────

    const objMap: Record<string, { tag: string; areas: number[] }> = {};
    for (const obj of allObjs) {
      if (obj.name === 'Others') continue;
      const key = `${obj.name}||${obj.tag}`;
      if (!objMap[key]) objMap[key] = { tag: obj.tag, areas: [] };
      objMap[key].areas.push(obj.bbox.width * obj.bbox.height);
    }

    const maxObjArea = Math.max(
      ...Object.values(objMap).map(v => v.areas.reduce((a, b) => a + b, 0)), 1
    );

    this.allObjectRows = Object.entries(objMap).map(([key, val]) => {
      const [name] = key.split('||');
      const totalArea = val.areas.reduce((a, b) => a + b, 0);
      const meta = TAG_META[val.tag] ?? { label: val.tag, color: '#94a3b8', bg: 'rgba(148,163,184,.1)' };
      return {
        name,
        tag:      val.tag,
        tagLabel: meta.label,
        tagColor: meta.color,
        tagBg:    meta.bg,
        areaPx:   Math.round(totalArea),
        areaPct:  Math.round((totalArea / this.totalImageArea) * 1000) / 10,
        count:    val.areas.length,
        avgArea:  Math.round(totalArea / val.areas.length),
        barW:     Math.round((totalArea / maxObjArea) * 100),
      };
    }).sort((a, b) => b.areaPx - a.areaPx);

    this.objectRows = this.showAllObjects ? this.allObjectRows : this.allObjectRows.slice(0, 10);
  }

  toggleShowAll(): void {
    this.showAllObjects = !this.showAllObjects;
    this.objectRows = this.showAllObjects
      ? this.allObjectRows
      : this.allObjectRows.slice(0, 10);
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  formatArea(px: number): string {
    return px >= 1_000_000
      ? `${(px / 1_000_000).toFixed(2)} Mpx`
      : `${(px / 1_000).toFixed(1)} Kpx`;
  }

  trackByKey(_: number, t: TagCoverage): string { return t.key; }
  trackByName(_: number, r: ObjectAreaRow): string { return `${r.tag}|${r.name}`; }
}
